import Button, { ButtonGroup, LoadingButton } from '@atlaskit/button';
import Form, { ErrorMessage, Field, FormFooter, FormHeader, FormSection } from '@atlaskit/form';
import { OptionsType } from '@atlaskit/select';
import TextArea from '@atlaskit/textarea';
import Textfield from '@atlaskit/textfield';
import * as React from 'react';
import {
  CreateCreditNoteRequest,
  CreditNoteRequestMetaData,
  CreditNotetype,
  InvoiceLineItem,
  LineItemSelect,
  MAX_MEMO_LENGTH,
  PaymentMethod,
  FinanceReasonCodeSelect,
} from '@atlassian/cssxp-types-billing';
import {
  InfoPanel,
  useFormSubmit,
  useNotification,
  useSidebar,
  successNotificationBuilder,
  ErrorComponents,
  MultipleLineItemFormField,
  useFeatureToggle,
  NotificationTypes,
} from '@css-xp/base';
import { createCreditNotesCCP } from '@atlassian/temp-proxies';
import { ApolloQueryResult } from '@apollo/client';
import { ENABLE_SUPPORT_TICKET_AND_NOTE } from '@atlassian/cssxp-utils-generic';
import './cancel-invoice.scss';

interface Props {
  invoiceId: string;
  invoiceNumber: string;
  transactionAccountId: string;
  taxExempt?: boolean;
  refundType: CreditNotetype;
  isCancelLineItems: boolean;
  lineItems?: Array<InvoiceLineItem>;
  paymentMethod?: PaymentMethod;
  invoiceStatus?: string;
  invoiceCreated?: number;
  refetch?: () => Promise<ApolloQueryResult<any>>;
}

interface FormData {
  memo: string;
  financeRefundReason: FinanceReasonCodeSelect;
  lineItems?: Array<LineItemSelect>;
  internalNote: string | null;
  supportTicketNumber: string | null;
  comments?: string;
}

interface ConstructRequestDataProps {
  transactionAccountId: string;
  invoiceId: string;
  refundType: CreditNotetype;
  entitlementIds: string[] | null;
  paymentMethod: string | null | undefined;
  formData: FormData;
  invoiceStatus: string | null | undefined;
}

enum CancelHeader {
  CANCEL_INVOICE = 'Void full invoice',
  CANCEL_LINE_ITEMS = 'Void line items',
}

enum CancelInfo {
  CANCEL_INVOICE = 'If there are unsuccessful payment attempts, this action will make the invoice null and void. To close the invoice, a credit note (not a refund) will be generated. ',
  CANCEL_INVOICE_MESSAGE = 'Subscription cancellations need to be made from the subscription page.',
  CANCEL_INVOICE_SUCCESS_MESSAGE = 'This invoice is now void. A credit note will be generated',
  CANCEL_LINE_ITEMS = 'If there are unsuccessful payment attempts, this action will make the selected line items null and void. To close the voided portion of the invoice, a credit note (not a refund) will be generated.',
  CANCEL_LINE_ITEMS_MESSAGE = 'Subscription cancellations need to be made from the subscription page.',
  CANCEL_LINE_ITEMS_SUCCESS_MESSAGE = 'The selected line items are now void. A credit note will be generated.',
}

const constructMetadata = (formData: FormData) => {
  const metadata: Record<string, string> = {};
  if (formData.internalNote) {
    metadata[CreditNoteRequestMetaData.INTERNAL_NOTE] = formData.internalNote;
  }
  if (formData.supportTicketNumber) {
    metadata[CreditNoteRequestMetaData.SUPPORT_TICKET_NUMBER] = formData.supportTicketNumber;
  }
  return metadata;
};

const constructRequestData = ({
  transactionAccountId,
  invoiceId,
  refundType,
  entitlementIds,
  paymentMethod,
  formData,
  invoiceStatus,
}: ConstructRequestDataProps) => {
  const requestData: CreateCreditNoteRequest = {
    transactionAccountId: transactionAccountId,
    invoice: invoiceId,
    memo: formData.memo,
    type: refundType,
    //@ts-ignore
    reason: 'IMMEDIATE_CANCELLATION_RETRIES'.toLowerCase(),
    //@ts-ignore
    entitlementIds: entitlementIds,
    paymentMethod: paymentMethod,
    metadata: constructMetadata(formData),
    invoiceStatus: invoiceStatus,
  };
  return requestData;
};

export const CancelInvoicePanel = ({
  invoiceId,
  invoiceNumber,
  transactionAccountId,
  refundType,
  isCancelLineItems,
  lineItems,
  paymentMethod,
  invoiceStatus,
  refetch,
}: Props) => {
  let options: OptionsType<LineItemSelect>;
  const { closeSidebar } = useSidebar();
  const { error: formError, isProcessing, submitForm, complete } = useFormSubmit<CreateCreditNoteRequest, any>({
    submitFormAction: createCreditNotesCCP,
    onComplete: () => {
      closeSidebar();
      refetch?.();
    },
  });

  const { sendSuccessNotification, notificationSent, setNotificationSent } = useNotification();
  const [isEnabled] = useFeatureToggle();

  const getCancelHeader = React.useCallback((): string => {
    if (isCancelLineItems) {
      return CancelHeader.CANCEL_LINE_ITEMS;
    } else return CancelHeader.CANCEL_INVOICE;
  }, [isCancelLineItems, refundType]);

  const getCancelInfo = React.useCallback((): string => {
    if (isCancelLineItems) {
      return CancelInfo.CANCEL_LINE_ITEMS;
    } else return CancelInfo.CANCEL_INVOICE;
  }, [isCancelLineItems, refundType]);

  const getCancelInfoMessage = React.useCallback(
    (): string => (isCancelLineItems ? CancelInfo.CANCEL_LINE_ITEMS_MESSAGE : CancelInfo.CANCEL_INVOICE_MESSAGE),
    [isCancelLineItems]
  );

  const getCancelInfoSuccessMessage = React.useCallback(
    (): string | JSX.Element =>
      isCancelLineItems ? CancelInfo.CANCEL_LINE_ITEMS_SUCCESS_MESSAGE : CancelInfo.CANCEL_INVOICE_SUCCESS_MESSAGE,
    [isCancelLineItems]
  );

  if (!transactionAccountId) {
    return (
      <InfoPanel
        type="error"
        message={
          <ErrorComponents.ErrorMessageOnInfoPanel
            title="We're unable to cancel invoice as no transaction account found for this invoice"
            message="No trasaction account found"
          />
        }
      />
    );
  }

  const getItemsToRefund = (formData: FormData): string[] | null => {
    let items: string[] | null = null;

    if (isCancelLineItems) {
      items = formData?.lineItems ? formData?.lineItems?.map((lineitem) => lineitem.value) : null;
    } else {
      items = lineItems?.map((lineitem) => lineitem.id ?? '') ?? null;
    }

    return items;
  };

  const handleFormSubmit = async (formData: FormData) => {
    const itemsToRefund = getItemsToRefund(formData);
    const requestData = constructRequestData({
      invoiceId,
      transactionAccountId,
      refundType,
      entitlementIds: itemsToRefund,
      //@ts-ignore
      paymentMethod,
      formData,
      invoiceStatus,
    });
    submitForm(requestData);
  };

  if (complete && !notificationSent) {
    const notificationOptions = successNotificationBuilder()
      .isVersion(NotificationTypes.FLAG)
      .withId('refund-processed-success')
      .withHeader('Action completed')
      .withMessage(getCancelInfoSuccessMessage())
      .withDismissAfter(5000)
      .build();
    sendSuccessNotification(notificationOptions);
    setNotificationSent(true);
  }

  if (isCancelLineItems && lineItems) {
    const lineItemsForRefund = lineItems?.filter((lineItem) => {
      return Number(lineItem?.subtotal) > 0;
    });
    options = lineItemsForRefund.map((lineItem) => {
      const description = lineItem?.description ? ` (${lineItem?.description})` : '';
      const label = `${lineItem?.entitlementNumber}${description}` ?? '';
      const lineItemSelect: LineItemSelect = {
        label: label,
        value: lineItem?.id ?? '',
      };
      return lineItemSelect;
    });
  }

  return (
    <div className="invoice-refund-ccp-form" style={{ maxWidth: '600px' }}>
      <Form onSubmit={handleFormSubmit}>
        {({ formProps }) => (
          <form {...formProps}>
            <FormHeader title={getCancelHeader()}></FormHeader>
            <FormSection>
              <InfoPanel type="information" title={getCancelInfo()} message={getCancelInfoMessage()} />
            </FormSection>
            <FormSection>
              {!!formError && (
                <InfoPanel
                  type="error"
                  message={
                    <ErrorComponents.ErrorMessageOnInfoPanel
                      title="We're unable to cancel invoice for the following reason"
                      message={formError}
                    />
                  }
                />
              )}
            </FormSection>

            <FormSection>
              <Field name="invoiceNumber" label="Invoice number">
                {() => <div>{invoiceNumber}</div>}
              </Field>
            </FormSection>

            <FormSection>
              {isCancelLineItems && (
                <MultipleLineItemFormField options={options} fieldName={`lineItems`} isRequired={true} />
              )}
            </FormSection>

            <FormSection>
              <Field<string, HTMLTextAreaElement>
                name="memo"
                label="Internal memo"
                isRequired={true}
                validate={(value: any) => (!(value && value.length < MAX_MEMO_LENGTH) ? 'INVALID' : undefined)}
              >
                {({ fieldProps, error }) => (
                  <div>
                    <TextArea
                      {...fieldProps}
                      placeholder={`Add notes (Max limit: ${MAX_MEMO_LENGTH} characters, internal memo is not shown in PDF)`}
                      name="memo"
                      testId={'refund-update-memo-field'}
                      isRequired={true}
                      isInvalid={error ? true : false}
                    />
                    {error && <ErrorMessage>{`Max limit: ${MAX_MEMO_LENGTH} characters`}</ErrorMessage>}
                  </div>
                )}
              </Field>
            </FormSection>

            {isEnabled(ENABLE_SUPPORT_TICKET_AND_NOTE) && (
              <FormSection>
                <Field
                  name="supportTicketNumber"
                  label="Support ticket number"
                  isRequired
                  validate={(value: any) => (value && value.length > 0 ? undefined : 'INVALID')}
                >
                  {({ fieldProps, error }) => (
                    <>
                      <Textfield {...fieldProps} name="supportTicketNumber" placeholder={'Add GSAC ticket number'} />
                      {error && <ErrorMessage>Enter a valid ticket number</ErrorMessage>}
                    </>
                  )}
                </Field>
              </FormSection>
            )}

            <FormFooter align="end">
              <ButtonGroup>
                <Button appearance="default" onClick={() => closeSidebar()} testId="cancel-btn">
                  Cancel
                </Button>
                <LoadingButton appearance="primary" type="submit" isLoading={isProcessing} testId="cancel-invoice">
                  {!isCancelLineItems ? 'Void invoice' : 'Void line items'}
                </LoadingButton>
              </ButtonGroup>
            </FormFooter>
          </form>
        )}
      </Form>
    </div>
  );
};
